import { useState, useEffect, useCallback } from "react";
import THEME from "../../../theme";
import { fs } from "../utils/styles";
import { finanzenApi } from "../services/finanzenApi";
import BwaTrendChart from "../components/BwaTrendChart";
import BwaKategorieVerlauf from "../components/BwaKategorieVerlauf";
import BwaJahresKpiKarten from "../components/BwaJahresKpiKarten";
import BwaKostenaufschluesselung from "../components/BwaKostenaufschluesselung";
import BwaJahresvergleichTabelle from "../components/BwaJahresvergleichTabelle";
import BwaSchaetzung from "../components/BwaSchaetzung";
import OriginaldokumenteListe from "../components/OriginaldokumenteListe";
import AnlagevermoegenVerwaltung from "../components/AnlagevermoegenVerwaltung";

const geld = (n) => n == null ? "–" : `${n.toFixed(2)} €`;
const monatJahr = (monat, jahr) => `${String(monat).padStart(2, "0")}/${jahr}`;

function VeraenderungBadge({ prozent }) {
  if (prozent == null) return null;
  return (
    <span style={{ marginLeft: 8, color: prozent >= 0 ? THEME.colors.success : THEME.colors.danger, fontSize: THEME.fontSize.sm }}>
      {prozent >= 0 ? "▲" : "▼"} {Math.abs(prozent)}%
    </span>
  );
}

// Schneidet trend.monate/serien auf einen Zeitraum ab "vonMonat/vonJahr" zu - fuer die beiden
// Verlaufs-Charts (Entwicklung/Kostenaufschluesselung), unabhaengig von den Jahres-KPIs/
// -Vergleichen, die weiterhin die volle Historie zeigen.
function filtereTrend(trend, vonMonat, vonJahr) {
  if (!trend || !trend.monate) return trend;
  const startIndex = trend.monate.findIndex(label => {
    const [m, j] = label.split("/").map(Number);
    return j > vonJahr || (j === vonJahr && m >= vonMonat);
  });
  if (startIndex <= 0) return trend;
  const monate = trend.monate.slice(startIndex);
  const serien = {};
  for (const [schluessel, serie] of Object.entries(trend.serien)) {
    serien[schluessel] = { ...serie, werte: serie.werte.slice(startIndex) };
  }
  return { monate, serien };
}

function findeWert(positionen, schluessel) {
  const p = (positionen || []).find(p => p.schluessel === schluessel);
  return p ? p.wert_monat : null;
}

function KpiKartenBwa({ positionen }) {
  const kennzahlen = [
    { schluessel: "umsatzerloese", label: "Umsatz (Monat)" },
    { schluessel: "rohertrag", label: "Rohertrag (Monat)" },
    { schluessel: "betriebsergebnis", label: "Betriebsergebnis (Monat)" },
    { schluessel: "ergebnis_vor_steuern", label: "Ergebnis vor Steuern (Monat)" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
      {kennzahlen.map(({ schluessel, label }) => {
        const wert = findeWert(positionen, schluessel);
        return (
          <div key={schluessel} style={{ ...fs.karte, marginBottom: 0 }}>
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 6px" }}>{label}</p>
            <p style={{ color: wert >= 0 ? THEME.colors.success : THEME.colors.danger, fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: 0 }}>{geld(wert)}</p>
          </div>
        );
      })}
    </div>
  );
}

// Nutzer-Wunsch (2026-08): frei waehlbarer Zeitraum, um jederzeit selbst nachzusehen, wie hoch
// Durchschnittskosten/-umsatz/-betriebsergebnis in einer bestimmten Spanne waren - rein
// clientseitig aus den ohnehin schon geladenen trend-Zeitreihendaten berechnet, kein eigener
// Backend-Aufruf noetig.
const DURCHSCHNITT_KENNZAHLEN = [
  { schluessel: "umsatzerloese", label: "Ø Umsatz" },
  { schluessel: "gesamtkosten", label: "Ø Gesamtkosten" },
  { schluessel: "betriebsergebnis", label: "Ø Betriebsergebnis" },
];

function berechneDurchschnitt(trend, vonMonat, vonJahr, bisMonat, bisJahr) {
  if (!trend || !trend.monate || trend.monate.length === 0) return null;
  const indices = [];
  trend.monate.forEach((label, i) => {
    const [m, j] = label.split("/").map(Number);
    const abVon = j > vonJahr || (j === vonJahr && m >= vonMonat);
    const bisEinschl = j < bisJahr || (j === bisJahr && m <= bisMonat);
    if (abVon && bisEinschl) indices.push(i);
  });
  if (indices.length === 0) return null;
  const werte = {};
  for (const { schluessel } of DURCHSCHNITT_KENNZAHLEN) {
    const serie = trend.serien[schluessel]?.werte || [];
    const vorhanden = indices.map(i => serie[i]).filter(w => w != null);
    werte[schluessel] = vorhanden.length > 0 ? vorhanden.reduce((a, b) => a + b, 0) / vorhanden.length : null;
  }
  return { anzahlMonate: indices.length, werte };
}

function DurchschnittZeitraum({ trend }) {
  const jahre = trend?.monate?.length
    ? [...new Set(trend.monate.map(l => Number(l.split("/")[1])))].sort()
    : [new Date().getFullYear()];
  const ersterMonat = trend?.monate?.[0];
  const [vonMonat, setVonMonat] = useState(ersterMonat ? Number(ersterMonat.split("/")[0]) : 1);
  const [vonJahr, setVonJahr] = useState(ersterMonat ? Number(ersterMonat.split("/")[1]) : jahre[0]);
  const heute = new Date();
  const [bisMonat, setBisMonat] = useState(heute.getMonth() + 1);
  const [bisJahr, setBisJahr] = useState(heute.getFullYear());

  const ergebnis = berechneDurchschnitt(trend, vonMonat, vonJahr, bisMonat, bisJahr);

  const monatSelect = (wert, setWert) => (
    <select style={{ ...fs.input, width: "auto" }} value={wert} onChange={e => setWert(Number(e.target.value))}>
      {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
    </select>
  );
  const jahrSelect = (wert, setWert) => (
    <select style={{ ...fs.input, width: "auto" }} value={wert} onChange={e => setWert(Number(e.target.value))}>
      {jahre.map(j => <option key={j} value={j}>{j}</option>)}
    </select>
  );

  return (
    <div style={fs.karte}>
      <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>Durchschnitt über Zeitraum</h3>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Von</span>
        {monatSelect(vonMonat, setVonMonat)} {jahrSelect(vonJahr, setVonJahr)}
        <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>bis</span>
        {monatSelect(bisMonat, setBisMonat)} {jahrSelect(bisJahr, setBisJahr)}
      </div>
      {!ergebnis && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Keine BWA-Daten in diesem Zeitraum.</p>}
      {ergebnis && (
        <>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 12px" }}>
            Basis: {ergebnis.anzahlMonate} Monat{ergebnis.anzahlMonate === 1 ? "" : "e"}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
            {DURCHSCHNITT_KENNZAHLEN.map(({ schluessel, label }) => {
              const wert = ergebnis.werte[schluessel];
              return (
                <div key={schluessel} style={{ ...fs.karte, marginBottom: 0 }}>
                  <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 6px" }}>{label}</p>
                  <p style={{ color: wert == null ? THEME.colors.textMuted : (wert >= 0 ? THEME.colors.success : THEME.colors.danger), fontSize: THEME.fontSize.xxl, fontWeight: 700, margin: 0 }}>{geld(wert)}</p>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function SusaTabelle({ konten }) {
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState("");
  if (!konten || konten.length === 0) return null;
  const gefiltert = suche.trim()
    ? konten.filter(k => `${k.konto_nr} ${k.beschriftung}`.toLowerCase().includes(suche.trim().toLowerCase()))
    : konten;
  return (
    <div style={fs.karte}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h3 style={{ color: THEME.colors.text, margin: 0 }}>Summen und Salden ({konten.length} Konten)</h3>
        <button style={{ ...fs.btnGray, width: "auto" }} onClick={() => setOffen(o => !o)}>{offen ? "▲ einklappen" : "▼ anzeigen"}</button>
      </div>
      {offen && (
        <>
          <input
            style={{ ...fs.input, marginTop: 12, marginBottom: 12, width: "100%", maxWidth: 320 }}
            placeholder="Konto oder Beschriftung suchen…"
            value={suche} onChange={e => setSuche(e.target.value)}
          />
          <div style={fs.tabelle}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr><th style={fs.th}>Konto</th><th style={fs.th}>Beschriftung</th><th style={{ ...fs.th, textAlign: "right" }}>Saldo</th></tr>
              </thead>
              <tbody>
                {gefiltert.map(k => (
                  <tr key={k.konto_nr}>
                    <td style={fs.td}>{k.konto_nr}</td>
                    <td style={fs.td}>{k.beschriftung}</td>
                    <td style={{ ...fs.td, textAlign: "right" }}>{geld(k.saldo)} {k.saldo_richtung}</td>
                  </tr>
                ))}
                {gefiltert.length === 0 && <tr><td style={fs.td} colSpan={3}>Keine Treffer.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function BwaSeite({ benutzerId }) {
  const [datei, setDatei] = useState(null);
  const [vorschau, setVorschau] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState("");
  const [importErgebnis, setImportErgebnis] = useState(null);
  const [verlauf, setVerlauf] = useState([]);
  const [trend, setTrend] = useState(null);
  const [detail, setDetail] = useState(null);
  const [kategorieAuswahl, setKategorieAuswahl] = useState(null);
  const [zeitraumVonMonat, setZeitraumVonMonat] = useState(1);
  const [zeitraumVonJahr, setZeitraumVonJahr] = useState(2025);
  const [syncLaedt, setSyncLaedt] = useState(false);
  const [syncMeldung, setSyncMeldung] = useState("");

  const ladeVerlauf = useCallback(() => {
    finanzenApi.bwaListe(benutzerId).then(setVerlauf).catch(() => {});
  }, [benutzerId]);
  const ladeTrend = useCallback(() => {
    finanzenApi.bwaTrend(benutzerId).then(setTrend).catch(() => {});
  }, [benutzerId]);

  useEffect(() => { ladeVerlauf(); ladeTrend(); }, [ladeVerlauf, ladeTrend]);

  const dateiWaehlen = (e) => {
    setDatei(e.target.files[0] || null);
    setVorschau(null);
    setImportErgebnis(null);
    setFehler("");
  };

  const vorschauLaden = async () => {
    if (!datei) return;
    setLaedt(true);
    setFehler("");
    try {
      setVorschau(await finanzenApi.bwaVorschau(benutzerId, datei));
    } catch (e) {
      setFehler(e.message);
    }
    setLaedt(false);
  };

  const importBestaetigen = async () => {
    if (!datei || !vorschau) return;
    setLaedt(true);
    setFehler("");
    try {
      const daten = await finanzenApi.bwaImportieren(benutzerId, datei, !!vorschau.bereits_vorhanden);
      setImportErgebnis(daten);
      setVorschau(null);
      setDatei(null);
      ladeVerlauf();
      ladeTrend();
    } catch (e) {
      setFehler(e.message);
    }
    setLaedt(false);
  };

  const emailSync = async () => {
    setSyncLaedt(true);
    setSyncMeldung("");
    try {
      const d = await finanzenApi.bwaEmailSync(benutzerId);
      if (d.uebersprungen) {
        setSyncMeldung("Erst vor Kurzem synchronisiert – nächster automatischer Versuch später.");
      } else {
        setSyncMeldung(`✓ ${d.gefunden} BWA-PDFs gefunden, ${d.neu} neu, ${d.aktualisiert} aktualisiert.${d.fehler.length > 0 ? ` (${d.fehler.length} Fehler)` : ""}`);
      }
      ladeVerlauf();
      ladeTrend();
    } catch (e) {
      setSyncMeldung(`Fehler: ${e.message}`);
    }
    setSyncLaedt(false);
  };

  const detailOeffnen = async (id) => {
    try {
      setDetail(await finanzenApi.bwaDetail(benutzerId, id));
    } catch (e) {
      setFehler(e.message);
    }
  };

  const loeschen = async (id) => {
    if (!window.confirm("Diesen BWA-Monat wirklich löschen?")) return;
    await finanzenApi.bwaLoeschen(benutzerId, id);
    if (detail?.id === id) setDetail(null);
    ladeVerlauf();
    ladeTrend();
  };

  return (
    <div>
      <BwaSchaetzung benutzerId={benutzerId} />
      <AnlagevermoegenVerwaltung benutzerId={benutzerId} />
      {verlauf.length > 0 && (
        <>
          <h2 style={{ color: THEME.colors.text, marginTop: 0 }}>Übersicht</h2>
          <BwaJahresKpiKarten trend={trend} />
          <DurchschnittZeitraum trend={trend} />
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Zeitraum der Diagramme ab:</span>
            <select style={{ ...fs.input, width: "auto" }} value={zeitraumVonMonat} onChange={e => setZeitraumVonMonat(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
            </select>
            <select style={{ ...fs.input, width: "auto" }} value={zeitraumVonJahr} onChange={e => setZeitraumVonJahr(Number(e.target.value))}>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(j => <option key={j} value={j}>{j}</option>)}
            </select>
          </div>
          <BwaTrendChart trend={filtereTrend(trend, zeitraumVonMonat, zeitraumVonJahr)} />
          <BwaKostenaufschluesselung trend={filtereTrend(trend, zeitraumVonMonat, zeitraumVonJahr)} />
          <BwaJahresvergleichTabelle trend={trend} />
          {kategorieAuswahl && <BwaKategorieVerlauf trend={trend} schluessel={kategorieAuswahl} onSchliessen={() => setKategorieAuswahl(null)} />}
        </>
      )}

      <div style={fs.karte}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 4 }}>
          <h3 style={{ color: THEME.colors.text, margin: 0 }}>BWA automatisch aus E-Mail</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {syncMeldung && <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{syncMeldung}</span>}
            <button style={fs.btn} onClick={emailSync} disabled={syncLaedt}>{syncLaedt ? "Synchronisiert…" : "🔄 Jetzt synchronisieren"}</button>
          </div>
        </div>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "6px 0 0" }}>
          Holt automatisch alle BWA-SuSa-PDFs aus den E-Mails des Steuerberaters (info@comefit.de) und importiert neue bzw. aktualisierte Monate.
        </p>
      </div>

      <div style={fs.karte}>
        <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>BWA-PDF manuell hochladen</h3>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>
          Falls eine BWA mal nicht automatisch per E-Mail ankommt: hier manuell hochladen (DATEV-Format) - Kennzahlen und Kontensalden werden automatisch ausgelesen.
        </p>
        <input type="file" accept=".pdf" onChange={dateiWaehlen} style={{ color: THEME.colors.text, marginBottom: 12 }} />
        <div style={{ display: "flex", gap: 12 }}>
          <button style={fs.btn} onClick={vorschauLaden} disabled={!datei || laedt}>{laedt ? "Lädt…" : "Vorschau anzeigen"}</button>
          {vorschau && <button style={{ ...fs.btn, background: THEME.colors.success }} onClick={importBestaetigen} disabled={laedt}>Import bestätigen</button>}
        </div>
        {fehler && <p style={{ color: THEME.colors.danger }}>{fehler}</p>}
      </div>

      {importErgebnis && (
        <div style={{ ...fs.karte, borderLeft: `4px solid ${THEME.colors.success}` }}>
          <p style={{ color: THEME.colors.text, margin: 0 }}>
            BWA für {monatJahr(importErgebnis.zusammenfassung.monat, importErgebnis.zusammenfassung.jahr)} gespeichert:{" "}
            {importErgebnis.zusammenfassung.anzahl_positionen} Kennzahlen, {importErgebnis.zusammenfassung.anzahl_susa_konten} Konten.
          </p>
        </div>
      )}

      {vorschau && (
        <div style={fs.karte}>
          <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>
            Vorschau: {monatJahr(vorschau.zusammenfassung.monat, vorschau.zusammenfassung.jahr)}
          </h3>
          {vorschau.bereits_vorhanden && (
            <p style={{ color: THEME.colors.warning }}>
              ⚠ Für diesen Monat wurde bereits am {vorschau.bereits_vorhanden.hochgeladen_am} eine BWA hochgeladen. Bestätigen ersetzt die vorherigen Werte.
            </p>
          )}
          {vorschau.zusammenfassung.warnungen.length > 0 && (
            <ul style={{ color: THEME.colors.warning, fontSize: THEME.fontSize.sm }}>
              {vorschau.zusammenfassung.warnungen.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
          <KpiKartenBwa positionen={vorschau.positionen} />
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Auf eine Zeile klicken, um den Verlauf dieser Kennzahl über alle Monate zu sehen.</p>
          <div style={fs.tabelle}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr><th style={fs.th}>Bezeichnung</th><th style={{ ...fs.th, textAlign: "right" }}>Monat</th><th style={{ ...fs.th, textAlign: "right" }}>Jahr bis Monat</th></tr>
              </thead>
              <tbody>
                {vorschau.positionen.map(p => (
                  <tr key={p.schluessel} onClick={() => setKategorieAuswahl(p.schluessel)} style={{ cursor: trend?.serien?.[p.schluessel] ? "pointer" : "default" }}>
                    <td style={fs.td}>{p.bezeichnung}</td>
                    <td style={{ ...fs.td, textAlign: "right" }}>{geld(p.wert_monat)}</td>
                    <td style={{ ...fs.td, textAlign: "right" }}>{geld(p.wert_ytd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={fs.karte}>
        <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>Hochgeladene Monate</h3>
        <div style={fs.tabelle}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={fs.th}>Monat</th>
                <th style={{ ...fs.th, textAlign: "right" }}>Gesamteinnahmen</th>
                <th style={{ ...fs.th, textAlign: "right" }}>Gesamtkosten</th>
                <th style={{ ...fs.th, textAlign: "right" }}>Ergebnis vor Steuer</th>
                <th style={fs.th}></th>
              </tr>
            </thead>
            <tbody>
              {verlauf.map(v => (
                <tr key={v.id}>
                  <td style={fs.td}>{monatJahr(v.monat, v.jahr)}</td>
                  <td style={{ ...fs.td, textAlign: "right" }}>
                    {geld(v.gesamteinnahmen)}
                    <VeraenderungBadge prozent={v.gesamteinnahmen_veraenderung_prozent} />
                  </td>
                  <td style={{ ...fs.td, textAlign: "right" }}>
                    {geld(v.gesamtkosten)}
                    <VeraenderungBadge prozent={v.gesamtkosten_veraenderung_prozent} />
                  </td>
                  <td style={{ ...fs.td, textAlign: "right" }}>
                    {geld(v.ergebnis_vor_steuern)}
                    <VeraenderungBadge prozent={v.ergebnis_vor_steuern_veraenderung_prozent} />
                  </td>
                  <td style={{ ...fs.td, textAlign: "right", whiteSpace: "nowrap" }}>
                    <button style={{ ...fs.btnGray, width: "auto", marginRight: 8 }} onClick={() => detailOeffnen(v.id)}>Anzeigen</button>
                    <button style={fs.btnDanger} onClick={() => loeschen(v.id)}>Löschen</button>
                  </td>
                </tr>
              ))}
              {verlauf.length === 0 && <tr><td style={fs.td} colSpan={5}>Noch keine BWA hochgeladen.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <div style={fs.karte}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ color: THEME.colors.text, margin: 0 }}>Detail: {monatJahr(detail.monat, detail.jahr)}</h3>
            <button style={{ ...fs.btnGray, width: "auto" }} onClick={() => setDetail(null)}>Schließen</button>
          </div>
          <KpiKartenBwa positionen={detail.positionen} />
          <div style={fs.tabelle}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr><th style={fs.th}>Bezeichnung</th><th style={{ ...fs.th, textAlign: "right" }}>Monat</th><th style={{ ...fs.th, textAlign: "right" }}>Jahr bis Monat</th></tr>
              </thead>
              <tbody>
                {detail.positionen.map(p => (
                  <tr key={p.schluessel} onClick={() => setKategorieAuswahl(p.schluessel)} style={{ cursor: trend?.serien?.[p.schluessel] ? "pointer" : "default" }}>
                    <td style={fs.td}>{p.bezeichnung}</td>
                    <td style={{ ...fs.td, textAlign: "right" }}>{geld(p.wert_monat)}</td>
                    <td style={{ ...fs.td, textAlign: "right" }}>{geld(p.wert_ytd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {detail && <SusaTabelle konten={detail.susa_konten} />}
      <OriginaldokumenteListe benutzerId={benutzerId} />
    </div>
  );
}
